// The key-value backend the offline store runs on (BL-2340): the contract the IndexedDB and
// in-memory adapters implement, so the store's behaviour is written once in the engine.

/**
 * The backend's tables.
 * - `children` indexes parented rows as `[t, u, kind, parentKey, childKey]`, so a parent's rows are
 *   one key range.
 * - `parentOf` is its reverse (`[t, u, kind, childKey]` → parentKey), so a re-put learns the old
 *   parent without reading the payload.
 * - `outboxIds` maps an entry id to its seq.
 * - `staged` holds staged files' metadata (BL-2421); the bytes live on the device filesystem.
 * - `lotStates` holds each lot's desired final state (BL-2681), keyed `[t, u, lotId]`.
 */
export type OfflineStoreTable =
  | 'meta'
  | 'records'
  | 'thumbnails'
  | 'outbox'
  | 'outboxIds'
  | 'children'
  | 'parentOf'
  | 'staged'
  | 'lotStates';

/** Every table, for a wipe and for the IndexedDB upgrade step. */
export const OFFLINE_STORE_TABLES: readonly OfflineStoreTable[] = [
  'meta',
  'records',
  'thumbnails',
  'outbox',
  'outboxIds',
  'children',
  'parentOf',
  'staged',
  'lotStates',
];

/** The tables that hold one scope's data, all keyed `[tenantId, userId, ...]`. */
export const SCOPED_TABLES = ['records', 'thumbnails', 'outbox', 'outboxIds', 'children', 'parentOf', 'staged', 'lotStates'] as const;

/** A compound key. Strings and numbers order as IndexedDB orders them (numbers first). */
export type OfflineStoreKey = (string | number)[];

/**
 * One write in an atomic batch. `add` fails the whole batch when the key already exists, so a
 * clash is loud rather than a silent overwrite.
 */
export type OfflineStoreWrite =
  | { type: 'put' | 'add'; table: OfflineStoreTable; key: OfflineStoreKey; value: unknown }
  | { type: 'delete'; table: OfflineStoreTable; key: OfflineStoreKey };

/**
 * How hard a batch is flushed. `strict` waits for the disk, for the queue and its bookkeeping,
 * which hold the only copy of the user's work. `relaxed` is for cached server data a sync can
 * fetch again.
 */
export type OfflineStoreDurability = 'strict' | 'relaxed';

/**
 * The persistence a store runs on. Values come back as copies (structured clone), and a batch
 * either lands whole or not at all.
 */
export interface OfflineStoreBackend {
  /**
   * @param table - the table to read.
   * @param key - the full key.
   * @returns a copy of the stored value, or undefined.
   */
  get(table: OfflineStoreTable, key: OfflineStoreKey): Promise<unknown>;
  /**
   * @param table - the table to read.
   * @param keys - the full keys, read in one transaction.
   * @returns a copy of each value in key order, undefined where absent.
   */
  getMany(table: OfflineStoreTable, keys: OfflineStoreKey[]): Promise<unknown[]>;
  /**
   * @param table - the table to read.
   * @param prefix - the leading key parts every returned key starts with.
   * @returns every matching entry, ordered by key.
   */
  scan(table: OfflineStoreTable, prefix: OfflineStoreKey): Promise<{ key: OfflineStoreKey; value: unknown }[]>;
  /**
   * Like {@link scan} without reading values, so a wipe never loads blobs or payloads.
   *
   * @param table - the table to read.
   * @param prefix - the leading key parts every returned key starts with.
   * @returns every matching key, ordered.
   */
  scanKeys(table: OfflineStoreTable, prefix: OfflineStoreKey): Promise<OfflineStoreKey[]>;
  /**
   * @param writes - the puts, adds and deletes to apply in one transaction.
   * @param options - the batch's durability; `strict` when omitted.
   * @returns once the batch has committed.
   * @throws when any write fails; nothing in the batch is applied.
   * @sideEffect Writes to the device store.
   */
  write(writes: OfflineStoreWrite[], options?: { durability?: OfflineStoreDurability }): Promise<void>;
  /**
   * @returns once every table is empty.
   * @sideEffect Deletes everything in the device store.
   */
  clearAll(): Promise<void>;
}

/**
 * Thrown by a store handle whose scope has since been wiped (a different user opened the store,
 * or the scope aged out) or whose connection the web view closed. Re-open the store from the
 * current session's tenant and user.
 */
export class OfflineStoreClosedError extends Error {
  constructor() {
    super('This offline store was closed or wiped after it was opened. Open it again for the current session.');
    this.name = 'OfflineStoreClosedError';
  }
}

/** The names a browser gives a write refused for want of space (the spec's, and Firefox's legacy one). */
const QUOTA_ERROR_NAMES = new Set(['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED']);

/**
 * Whether a store failure was the device running out of storage (BUG-2065): a
 * QuotaExceededError anywhere in the cause chain, as an IndexedDB abort wraps it. Read by name,
 * not class, so a DOMException from another realm still counts.
 *
 * @param err - the failure.
 * @returns true when the write was refused for want of space.
 */
export function isOfflineStoreFullError(err: unknown): boolean {
  for (let cause: unknown = err, depth = 0; cause != null && depth < 8; depth += 1) {
    const name = (cause as { name?: unknown }).name;
    if (typeof name === 'string' && QUOTA_ERROR_NAMES.has(name)) return true;
    cause = (cause as { cause?: unknown }).cause;
  }
  return false;
}
